import { Box, Button, CircularProgress, Grid } from '@material-ui/core';
import { ClickIcon, Typography } from '@room-match/shared-ui-components';
import Stepper from 'components/Stepper/Stepper';
import Compressor from 'compressorjs';
import heic2any from 'heic2any';
import React, { useRef, useState } from 'react';
import useWindowDimensions from 'shared/hooks/useWindowDimenstion';
import { IOutput } from 'shared/interfaces/IProfile';
import { useStyles } from './ProfileImageAdult.styles';
import { ProfileImageCropper } from './ProfileImageCropper';

const imageMimeType = /image\/(png|jpg|jpeg)/i;

type Props = {
  onFileSelected: (
    fname: string,
    type: string,
    size: string,
    file?: Blob,
    imageAttr?: { width: number; height: number },
  ) => void;
};

const maxFileSize = 100000; // 10MB

const ProfileImageAdult: React.FC<Props> = ({ onFileSelected }) => {
  const classes = useStyles();
  const [file, setFile] = useState<File | null>(null);
  const [fileDataURL, setFileDataURL] = useState(null);
  const [cropper, setCropper] = useState<any>(null);
  const { width } = useWindowDimensions();
  const inputFile = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const handleOpenFileWindow = () => {
    if (inputFile.current) {
      inputFile.current.click();
    }
  };

  const getNormalizedFile = (file: File) => {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.6,
        maxWidth: 4096,
        maxHeight: 4096,
        success(normalizedFile) {
          resolve(normalizedFile);
        },
        error(error) {
          reject(error);
        },
      });
    });
  };

  const changeHandler = async (e: any) => {
    let file = e.target.files[0];
    // if (!file?.type.match(imageMimeType)) {
    //   alert('Image mime type is not valid');
    //   return;
    // }

    // if (file.size > maxFileSize) {
    //   alert('Image size exeeded the limit of 10MB');
    //   return; // do not process the file if it exceeds the size limit
    // }

    if (!file) return;
    const object: any = {};
    for (const key in file) {
      object[key] = file[key];
    }
    // captureMessage({
    //   message: 'user uploaded the following image',
    //   data: object,
    // });
    setIsLoading(true);
    if (file.type === 'image/heic' || file.type === 'image/heif') {
      const fileName = file.name;
      file = await heic2any({
        blob: file,
        toType: 'image/jpeg',
      });
      file.name = fileName + '.jpg';
    }

    setFile(file);

    let fileReader: any,
      isCancel = false;
    if (file) {
      fileReader = new FileReader();
      const image = new Image();
      fileReader.onload = (e: any) => {
        const { result } = e.target;
        image.src = fileReader.result as string;
        setFileDataURL(result);
        setIsLoading(false);
      };

      getNormalizedFile(file).then((normalizedFile) => fileReader.readAsDataURL(normalizedFile));
    }
    return () => {
      isCancel = true;
      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort();
      }
    };
  };

  const getCropData = (data: IOutput) => {
    onFileSelected(file?.name || data.file_name, data.file_type, data.file_size, data.attachment, {
      width: parseInt(data.file_height),
      height: parseInt(data.file_height),
    });
  };

  // useEffect(() => {
  //   captureMessage('user landed on profile image page');
  // }, []);

  return (
    <Box component="div" className={classes.root}>
      <Grid item className={classes.header}>
        <Typography fontSize={24} style={{ marginBottom: 10 }}>
          Profile Image
        </Typography>
        <Stepper steps={3} active={fileDataURL ? 2 : 1} />
      </Grid>
      <Grid item className={classes.form}>
        <div style={{ height: 0, width: 0, overflow: 'hidden' }}>
          <input
            ref={inputFile}
            id="contained-input-file"
            type="file"
            accept="image/*, .heic, .heif"
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              changeHandler(e);
            }}
          />
        </div>

        <Box component="div" className={classes.imgContainer}>
          {isLoading && (
            <Box
              style={{
                position: 'absolute',
                width: '100%',
                top: '40%',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <CircularProgress size={35} />
              <Typography> Processing image...</Typography>
            </Box>
          )}
          {fileDataURL === null ? (
            <React.Fragment />
          ) : (
            <ProfileImageCropper
              src={fileDataURL ? fileDataURL : ''}
              cropper={cropper}
              setCropper={setCropper}
              handleCrop={getCropData}
            />
          )}
          {/* <img className={classes.img} src={fileDataURL ? fileDataURL : ''}></img> */}
        </Box>
        {fileDataURL === null ? (
          ''
        ) : width > 960 ? (
          <Box component="div" className={classes.tooltipContainer}>
            <ClickIcon />
            <Typography style={{ textAlign: 'center' }}>
              Use your cursor to drag & reposition inside the frame
            </Typography>
          </Box>
        ) : (
          <Box component="div" className={classes.tooltipContainer}>
            <Typography style={{ textAlign: 'center' }}>
              You can pinch to zoom & drag to reposition inside the frame
            </Typography>
          </Box>
        )}

        <Button className={classes.btn} onClick={handleOpenFileWindow} disabled={isLoading}>
          {file ? 'Change Image' : 'Upload Image'}
        </Button>
      </Grid>
    </Box>
  );
};

export default ProfileImageAdult;
